import { useState } from "react";
import styles from "./MusicPlayer.module.css";
import { useSelector } from "react-redux";
import Iframe from "react-iframe";
import ServiceSelector from "./ServiceSelector/ServiceSelector";
import CatalogItemMeta from "./CatalogItemMeta/CatalogItemMeta";
import AmbientBubbles from "../AmbientBubbles/AmbientBubbles";
import {
  formatReleaseCountdown,
  formatReleaseFullDate,
  formatTypeLabel,
  getEffectivePreReleaseMessage,
  isUpcomingRelease,
} from "../../Hooks/catalogPresentation";
import { parseIframeCustomAttributesForLayout } from "../../Hooks/parseIframeCustomAttributes";
import { toIframeSandboxList } from "../../Hooks/externalNavigation";
import { mergeEmbedUrl } from "../../Hooks/embedUrlQuery";

const MusicPlayer = (props) => {
  const { serviceSelected } = useSelector((state) => state.musicPlayer);
  const { serviceEmbedJSXObj, serviceEmbedQueryObj } = useSelector(
    (state) => state.catalogData,
  );
  const isFeatured = props.isFeaturedPlaylist;
  const catalogItem = props.catalogItem || null;
  // "full" (default) uses full-size iframe attrs; "compact" uses @@@small@@@ when set
  const embedLayout = props.embedLayout === "compact" ? "compact" : "full";
  const showMeta =
    props.showMeta !== false &&
    (catalogItem || props.artist || props.releaseDate || props.type);
  const [aboutOpen, setAboutOpen] = useState(false);
  const serviceEmbedJSX =
    serviceEmbedJSXObj && serviceEmbedJSXObj.hasOwnProperty(serviceSelected)
      ? serviceEmbedJSXObj[serviceSelected]
      : null;

  const outputIframeAttributesObj = parseIframeCustomAttributesForLayout(
    serviceEmbedJSX,
    embedLayout,
  );

  const outputIframeStyles =
    outputIframeAttributesObj &&
    outputIframeAttributesObj.hasOwnProperty("styles") &&
    outputIframeAttributesObj.styles
      ? outputIframeAttributesObj.styles
      : {};

  // Same height resolution as the live embed (incl. Admin full/small sections)
  const DEFAULT_IFRAME_HEIGHT = 300;
  const embedIframeHeight = (() => {
    const raw = outputIframeAttributesObj?.height;
    if (raw == null || raw === "") return DEFAULT_IFRAME_HEIGHT;
    const n = parseInt(String(raw).replace(/px/gi, "").trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_IFRAME_HEIGHT;
  })();

  // react-iframe requires sandbox as a string[] (not a space-joined string)
  const iframeSandbox = toIframeSandboxList(outputIframeAttributesObj.sandbox);

  const rawSourceURL =
    props.hasOwnProperty("sourceURLObj") &&
    props.sourceURLObj &&
    props.sourceURLObj.hasOwnProperty(serviceSelected) &&
    props.sourceURLObj[serviceSelected] &&
    props.sourceURLObj[serviceSelected].trim() !== ""
      ? props.sourceURLObj[serviceSelected]
      : false;

  // Overlay service-level default query params (e.g. show_teaser=false) on the
  // per-item embed URL. Service keys win; item identity query (e.g. SC url=) stays.
  const serviceDefaultQuery =
    serviceEmbedQueryObj &&
    serviceEmbedQueryObj.hasOwnProperty(serviceSelected)
      ? serviceEmbedQueryObj[serviceSelected]
      : "";
  const sourceURL = rawSourceURL
    ? mergeEmbedUrl(rawSourceURL, serviceDefaultQuery)
    : rawSourceURL;

  const activeTabAndURL = {
    sourceURL,
    serviceName: serviceSelected,
  };

  let isValidUrl = false;

  /* eslint-disable */
  try {
    isValidUrl = Boolean(new URL(activeTabAndURL.sourceURL));
  } catch (err) {}
  /* eslint-enable */

  const metaItem =
    catalogItem ||
    (showMeta
      ? {
          title: props.title,
          artist: props.artist,
          releaseDate: props.releaseDate || props.realeaseDate,
          type: props.type,
          tags: props.tags,
          description: props.description,
          notes: props.notes,
          credits: props.credits,
          preReleaseMessage: props.preReleaseMessage,
        }
      : null);

  const upcoming = metaItem ? isUpcomingRelease(metaItem) : false;
  const dropDateLabel = upcoming ? formatReleaseFullDate(metaItem) : "";
  const countdownLabel = upcoming ? formatReleaseCountdown(metaItem) : "";
  const releaseTitle =
    (metaItem && metaItem.title) || props.title || "New release";
  const typeLabel =
    metaItem && metaItem.type ? formatTypeLabel(metaItem.type) : "";
  const preReleaseMessage = getEffectivePreReleaseMessage(
    (metaItem && metaItem.preReleaseMessage) || props.preReleaseMessage || "",
  );

  return (
    <div
      className={
        styles["music-player-container"] +
        " " +
        styles["stock-box-shadow-" + props.showStockBoxShadow] +
        " " +
        styles[isFeatured ? "featured" : "not-featured"] +
        (showMeta ? " " + styles["has-meta"] : "") +
        (upcoming ? " " + styles["is-upcoming"] : "")
      }
      style={props.passedStyles}
    >
      {upcoming ? (
        <div
          className={styles["upcoming-panel"]}
          role="status"
          aria-live="polite"
          style={{
            // Match the music embed iframe height for this service + layout
            // (full vs compact/small Admin attrs via embedLayout).
            // Adding 60px to simulate the size of the Music Player header and footer
            minHeight: embedIframeHeight,
            ["--ms1-upcoming-min-height"]: `${embedIframeHeight + 60}px`,
          }}
        >
          <AmbientBubbles />
          <p className={styles["upcoming-eyebrow"]}>Upcoming Release</p>
          {typeLabel && (
            <span className={styles["upcoming-type"]}>{typeLabel}</span>
          )}
          <h3 className={styles["upcoming-title"]}>{releaseTitle}</h3>
          <p className={styles["upcoming-lead"]}>{preReleaseMessage}</p>
          {dropDateLabel && (
            <p className={styles["upcoming-date"]}>
              Drops <strong>{dropDateLabel}</strong>
            </p>
          )}
          {countdownLabel && (
            <p className={styles["upcoming-countdown"]}>{countdownLabel}</p>
          )}
        </div>
      ) : (
        <>
          {showMeta && metaItem ? (
            <CatalogItemMeta
              item={metaItem}
              title={props.title}
              compact={!!props.compactMeta}
              maxTags={props.maxTags != null ? props.maxTags : 2}
              showAbout={props.showAbout !== false}
              showNowPlayingHint={!!props.showNowPlayingHint}
              zone="header"
            />
          ) : (
            props.title && <h3>{props.title}</h3>
          )}
          {!props.hideServiceSelector && (
            <div className={styles["music-player-selector-wrap"]}>
              <ServiceSelector sourceURLObj={props.sourceURLObj} />
            </div>
          )}
          <div
            className={
              styles["music-player-wrap"] +
              " " +
              styles["player-frame-1-" + props.showPlayerFrame1]
            }
          >
            {!activeTabAndURL ||
              (!isValidUrl && (
                <div
                  className={
                    styles["music-service-container"] +
                    " " +
                    styles["no-service-container"]
                  }
                  style={props.passedStyles}
                >
                  <h3>
                    Unfortunately, this item is not listed on{" "}
                    {serviceSelected ? serviceSelected : "this service"}. Please
                    select a different service to access this content.
                  </h3>
                </div>
              ))}
            {activeTabAndURL && activeTabAndURL.sourceURL && isValidUrl && (
              <div
                className={
                  styles.tab +
                  " " +
                  styles["active-" + !!activeTabAndURL.serviceName]
                }
              >
                {" "}
                <div
                  className={styles["music-service-container"]}
                  style={props.passedStyles}
                >
                  <Iframe
                    styles={{
                      border: "none",
                      borderRadius: "30px",
                      boxShadow:
                        "-2px -2px 3px -2px black, 2px 2px 3px -2px white",
                      ...outputIframeStyles,
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                    }}
                    src={activeTabAndURL.sourceURL}
                    width="100%"
                    max-width={
                      outputIframeAttributesObj.maxWidth
                        ? outputIframeAttributesObj.maxWidth
                        : "100%"
                    }
                    height={String(embedIframeHeight)}
                    max-height={
                      outputIframeAttributesObj.maxHeight
                        ? outputIframeAttributesObj.maxHeight
                        : ""
                    }
                    scrolling={
                      outputIframeAttributesObj.scrolling
                        ? outputIframeAttributesObj.scrolling
                        : "no"
                    }
                    frameborder={
                      outputIframeAttributesObj.frameborder
                        ? outputIframeAttributesObj.frameborder
                        : "no"
                    }
                    allow={
                      "encrypted-media *; " +
                      (outputIframeAttributesObj.allow
                        ? outputIframeAttributesObj.allow
                        : "autoplay")
                    }
                    // Block embeds from navigating the standalone PWA shell
                    // (iOS white in-app browser trap). Popups can still escape.
                    sandbox={iframeSandbox}
                    title={
                      outputIframeAttributesObj.title
                        ? outputIframeAttributesObj.title
                        : props.title
                          ? `${props.title} player`
                          : "Music Player"
                    }
                    loading={
                      outputIframeAttributesObj.loading
                        ? outputIframeAttributesObj.loading
                        : "lazy"
                    }
                    allowtransparency={
                      outputIframeAttributesObj.allowtransparency
                        ? outputIframeAttributesObj.allowtransparency
                        : "true"
                    }
                    allowfullscreen={
                      outputIframeAttributesObj.allowfullscreen
                        ? outputIframeAttributesObj.allowfullscreen
                        : "true"
                    }
                  >
                    You need a Frames Capable browser to view this content.
                  </Iframe>
                </div>{" "}
              </div>
            )}
          </div>
          {showMeta && metaItem && (
            <CatalogItemMeta
              item={metaItem}
              title={props.title}
              compact={!!props.compactMeta}
              maxTags={props.maxTags != null ? props.maxTags : 2}
              showAbout={props.showAbout !== false}
              zone="footer"
              aboutOpen={aboutOpen}
              onAboutOpenChange={setAboutOpen}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MusicPlayer;
